import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, jsonBody } from '../api';
import type { SymbolView } from '../types';

function SymbolSettings({ symbol }: { symbol: SymbolView }) {
  const [draft, setDraft] = useState(symbol);
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: () => {
      const { symbol: _symbol, ...patch } = draft;
      return api<SymbolView>(`/api/config/symbols/${encodeURIComponent(symbol.symbol)}`, { method: 'PATCH', ...jsonBody(patch) });
    },
    onSuccess: async () => { await queryClient.invalidateQueries({ predicate: (query) => ['symbols', 'account', 'accounts'].includes(String(query.queryKey[0])) }); }
  });
  const fields = [
    ['emaPeriod', 'EMA 周期', 2, 500, 1], ['atrPeriod', 'ATR 周期', 2, 500, 1],
    ['entryAtrMultiplier', '开仓 ATR 倍数', 0.01, 100, 0.01], ['extremeAtrMultiplier', '加仓 / 止盈 ATR 倍数', 0.01, 100, 0.01],
    ['baseAmountUsdt', '基础开仓金额 / USDT', 0.01, undefined, 0.01]
  ] as const;
  return <form className="panel symbol-settings" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
    <div className="symbol-settings-head"><div><h2>{symbol.symbol.split('-')[0]}</h2><span>USDT 永续 · 全账户共用</span></div>
      <label className="toggle"><input type="checkbox" aria-label={`${symbol.symbol} 全局策略开关`} checked={draft.enabled} disabled={save.isPending} onChange={(event) => { setDraft({ ...draft, enabled: event.target.checked }); save.reset(); }} /><i /></label>
    </div>
    <div className="symbol-settings-fields">{fields.map(([field, label, min, max, step]) => <label key={field}>
      <span>{label}</span><input aria-label={`${symbol.symbol} ${label}`} type="number" required min={min} max={max} step={step} value={Number.isNaN(draft[field]) ? '' : draft[field]} disabled={save.isPending}
        onChange={(event) => { setDraft({ ...draft, [field]: event.target.valueAsNumber }); save.reset(); }} />
    </label>)}</div>
    <div className="symbol-settings-foot"><span>开仓金额 = 基础金额 × 账户倍数</span><button className="primary-button" disabled={save.isPending}>{save.isPending ? '保存中…' : '保存参数'}</button></div>
    {save.error ? <div className="form-error" role="alert">{save.error.message}</div> : null}
    {save.isSuccess ? <div className="settings-saved" role="status">已保存，下次策略周期生效</div> : null}
  </form>;
}

export function StrategyPage() {
  const symbols = useQuery({ queryKey: ['symbols'], queryFn: () => api<SymbolView[]>('/api/config/symbols') });
  return <div className="page-stack page-enter">
    <div className="page-heading"><div><h1>币种策略参数</h1><p>按币种调整，全账户使用同一组参数</p></div></div>
    <p className="scope-note">周期单位为 4H K 线。开仓倍数用于多空入场；加仓 / 止盈倍数用于多单加仓与空单止盈。至少使用 1,000 根已收盘 K 线预热，较长周期自动增加历史长度。</p>
    {symbols.isLoading ? <div className="empty-state">正在加载参数…</div> : null}
    {symbols.error ? <div className="error-state">{symbols.error.message}</div> : null}
    <div className="symbol-settings-grid">{symbols.data?.map((symbol) => <SymbolSettings key={symbol.symbol} symbol={symbol} />)}</div>
  </div>;
}
